<template>
  <q-toolbar
    id="navbar"
    class="bg-white text-primary"
    :class="{ 'q-pl-none': $q.screen.lt.md }"
    v-bind="$attrs"
  >
    <template v-if="!mobileDrawerOpen">
      <re-button
        v-if="showMobileBackButton"
        class="lt-md text-black"
        icon="mdi-chevron-left"
        flat
        size="md"
        @click="handleMobileBack"
      />
      <p
        v-if="mobileTitle"
        class="text-black text-bold q-my-auto"
      >
        {{ mobileTitle }}
      </p>
    </template>
    <template v-if="mobileDrawerOpen || !mobileTitle">
      <q-btn
        :to="{
          name: email ? 'base' : 'login',
          query: { resetCurRequest: true },
        }"
        flat
        padding="0 .5em"
      >
        <img
          :src="logo"
          class="logo"
          width="148"
        />
      </q-btn>
    </template>
    <div
      v-if="$q.screen.lt.md"
      class="row items-center no-wrap q-ml-auto"
    >
      <re-button
        v-if="authenticated && talkjs.enabled"
        tag="router-link"
        size="sm"
        icon="mdi-face-agent"
        class="inbox-button q-mx-md"
        :to="{ name: 'inbox', query: { resetCurRequest: true } }"
        :class="{ 'inbox-button--unread': talkjs.totalUnreads }"
      />
      <re-button
        v-if="props.hasSideMenu"
        :class="{ 'bg-blue-grey-2': menuOpen }"
        size="sm"
        icon="mdi-menu"
        @click="menuOpen = !menuOpen"
      />
    </div>

    <template v-if="$q.screen.gt.sm">
      <go-to-dvm-input class="q-ml-sm" />
      <div class="q-ml-auto row no-wrap">
        <new-request-button
          v-if="authenticated && newRequestRelevant"
          class="col-xs-12 col-sm-auto q-ml-md"
          :label="newRequestButtonLabel"
          size="sm"
          :collapse-to-project="true"
        >
          <q-tooltip
            v-if="isCompact"
            class="bg-accent"
          >
            {{ t('dashboard.new_request.title') }}
          </q-tooltip>
        </new-request-button>
        <re-button
          v-if="authenticated && talkjs.enabled"
          tag="router-link"
          size="sm"
          icon="mdi-face-agent"
          class="inbox-button q-ml-md"
          :to="{ name: 'inbox', query: { resetCurRequest: true } }"
          :class="{ 'inbox-button--unread': talkjs.totalUnreads }"
        />
        <re-button
          v-if="!isLoginPage"
          icon="mdi-account"
          size="sm"
          class="q-ml-md"
        >
          <template #overlay>
            <q-popup-proxy>
              <navbar-items />
            </q-popup-proxy>
          </template>
        </re-button>
      </div>
    </template>
    <template v-if="isLoginPage">
      <lang-selector size="xs" />
    </template>
  </q-toolbar>

  <router-view
    v-if="$q.screen.lt.md"
    v-slot="{ Component }"
    name="navigation"
  >
    <component
      :is="Component"
      :navbar-mode="true"
    />
  </router-view>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/useConfig'
import { useCurUser } from '@/composables/useCurUser'
import { useValuerSidebarDrawer } from '@/composables/useValuerSidebarDrawer'
import { talkjsKey } from '@/keys'
import enums from '@/plugins/enums'

import GoToDvmInput from './GoToDvmInput.vue'
import LangSelector from './LangSelector.vue'
import NavbarItems from './NavbarItems.vue'
import NewRequestButton from './NewRequestButton.vue'

const menuOpen = defineModel('menuOpen', { type: Boolean, required: true })
const { t, te } = useI18n()
const $q = useQuasar()

const talkjs = inject(talkjsKey)!
const { mobileDrawerOpen } = useValuerSidebarDrawer()

const route = useRoute()
const config = useConfig()
const { email, accessibleModules, authenticated } = useCurUser()

const props = defineProps({
  hasSideMenu: {
    type: Boolean,
    default: false,
  },
})

const newRequestRelevant = computed(() => {
  return (
    !accessibleModules.value.includes(enums.modules.CAT) ||
    !['dashboard', 'catalog-start'].includes(route.name as string)
  )
})
const isCompact = computed(() => $q.screen.sm)

const newRequestButtonLabel = computed(() => {
  const isOnlyCatalogUser =
    accessibleModules.value.length === 1 && accessibleModules.value.includes(enums.modules.CAT)
  const label = isOnlyCatalogUser ? t('catalog.sidebar.new_button') : t('dashboard.new_request.title')
  return isCompact.value ? '' : label
})

const mobileTitle = computed(() => {
  if ($q.screen.gt.sm) return false
  const i18nParams: Record<string, unknown> = {}
  const title = route.meta?.mobile?.title
  if (title) {
    i18nParams[title] = route.query[title]
    return te(title) ? t(title, { ...i18nParams }) : false
  }
  return false
})

const showMobileBackButton = computed(() => {
  // Show back button on mobile for valuer-app routes (opens drawer) or routes with prevRoute
  return $q.screen.lt.md && route.meta?.mobile?.showValuerSidebar
})

function handleMobileBack() {
  // If we have a drawer injected (valuer-app routes), open it
  if (route.meta?.mobile?.showValuerSidebar) {
    mobileDrawerOpen.value = !mobileDrawerOpen.value
  }
}

const isLoginPage = computed(() => route.name === 'login')

const logo = computed(() => {
  switch (config.THEME_ID) {
    case 'ing':
      return new URL('@/assets/img/themes/rockestate-ing.svg', import.meta.url).href
    default:
      return new URL('@/assets/icons/rockestate-logo-horizontal-dark.svg', import.meta.url).href
  }
})
</script>

<style lang="scss" scoped>
.inbox-button {
  position: relative;

  &--unread {
    &::after {
      content: '';
      position: absolute;
      top: 10px;
      right: 10px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--q-warning);
    }
  }
}
#navbar {
  // padding: 0 15px;
  border-bottom: 1px solid $border;
  @media print {
    display: none;
  }
}
</style>
